/* Status vocabularies, ported from the mobile app's
 * `lib/features/shared/domain/enums.dart`.
 *
 * The parsing is deliberately tolerant. Go writes PascalCase
 * (`types.JobStatusPendingUserSignOff` == "PendingUserSignOff"), while parts of
 * the wire history carry kebab- and snake-cased variants. A strict parser would
 * not throw — it would quietly classify every unknown value as "pending" and
 * blank whole screens, which is exactly the failure mode this normalisation
 * exists to prevent.
 *
 * The WORDING of every status lives in shoku-ui's statusTone table, not here:
 * the pill used to say "Awaiting Sign-off" while the section heading two lines
 * above it said "Awaiting sign-off", because the two came from different files.
 */

import { labelFor } from "@ui/statusTone";
import { LAUNCH_TRADES, TRADES, tradeLabel, type Trade } from "@ui/react/tradeIcons";

export type JobStatus =
  | "pending"
  | "quoted"
  | "accepted"
  | "scheduled"
  | "inProgress"
  | "paymentProviderPendingResult"
  | "userPaid"
  | "pendingUserSignOff"
  | "completed"
  | "completedAndPaid"
  | "cancelled";

const JOB_STATUSES: readonly JobStatus[] = [
  "pending",
  "quoted",
  "accepted",
  "scheduled",
  "inProgress",
  "paymentProviderPendingResult",
  "userPaid",
  "pendingUserSignOff",
  "completed",
  "completedAndPaid",
  "cancelled",
];

/** Strips case and every separator, so "user-paid" == "USER_PAID" == "userPaid". */
function squash(value: string): string {
  return value.toLowerCase().replace(/[-_\s]/g, "");
}

export function parseJobStatus(value: string | null | undefined): JobStatus {
  if (!value) return "pending";
  const normalized = squash(value);
  const match = JOB_STATUSES.find((status) => status.toLowerCase() === normalized);
  return match ?? "pending";
}

export function jobStatusLabel(status: JobStatus): string {
  return labelFor("job", status);
}

export type JobBookingMode = "marketplace" | "direct";

/** Anything not recognisably a direct booking is a marketplace job — matching
 *  contractor-api, which defaults an empty bookingMode to marketplace before it
 *  ever reaches the wire (handlers/job/job.go). */
export function parseJobBookingMode(value: string | null | undefined): JobBookingMode {
  return squash(value ?? "") === "direct" ? "direct" : "marketplace";
}

/**
 * The server's `types.FundsHeld` (shared/pkg/types/job.go), mirrored exactly.
 *
 * A marketplace job holds funds only at `userPaid`: `accepted` and `scheduled`
 * mean a quote was accepted (and maybe a visit booked), not that any money
 * moved. A direct booking never passes through `userPaid` at all — user-api's
 * Paystack webhook mirrors it into a Job as `scheduled` only AFTER the reserve
 * succeeded, so for a direct booking `scheduled` already means paid.
 *
 * This has bitten in both directions. Too loose let a contractor work a job
 * with no hold on the homeowner's card. Too strict — testing status alone —
 * left every paid direct booking with no "Start work" button at all, on a
 * screen where the server would have accepted the call.
 */
export function fundsHeld(status: JobStatus, bookingMode: JobBookingMode): boolean {
  return status === "userPaid" || (bookingMode === "direct" && status === "scheduled");
}

/** Mirrors contractor-api's Start guard: funds held and nothing else. There is
 *  deliberately no clock here — the server imposes none, so a contractor who
 *  arrives early may start. */
export function canStartWork(status: JobStatus, bookingMode: JobBookingMode): boolean {
  return fundsHeld(status, bookingMode);
}

/**
 * Mirrors contractor-api's Complete guard (`InProgress || FundsHeld`): funds
 * reserved via preauth are completable without ever passing through InProgress.
 *
 * For a DIRECT booking the server ALSO rejects TOO_EARLY until the slot's start
 * time has passed. That clock lives on the booking, not the job, so only the
 * booking screen can mirror it — see BookingDetailScreen.
 */
export function canMarkComplete(status: JobStatus, bookingMode: JobBookingMode): boolean {
  return status === "inProgress" || fundsHeld(status, bookingMode);
}

export function isAwaitingSignOff(status: JobStatus): boolean {
  return status === "pendingUserSignOff";
}

/** Once true, the job is done for good: no more messages, no more quotes, no
 *  more scheduling. Two statuses exist because `completedAndPaid` is stamped
 *  later, asynchronously, once the payment capture settles. */
export function isJobCompleted(status: JobStatus): boolean {
  return status === "completed" || status === "completedAndPaid";
}

/**
 * Mirrors `types.OpenJobStatuses` (shared/pkg/types/enums.go), the set
 * contractor-api's `POST /quotes` gates on — everything else is 400
 * INVALID_STATUS. `quoted` is in it: the first quote by anyone flips the job
 * and the rest may still bid. Accepting a quote stamps `accepted` and paying
 * stamps `userPaid`, so a won job is closed to quotes from the moment of
 * acceptance, in the winner's thread as much as the losers'.
 *
 * Deliberately an ALLOW-list of the open statuses rather than a deny-list of
 * the closed ones, so a status added to the lifecycle later closes quoting
 * here by default instead of silently staying quotable. It reads a missing or
 * unrecognised status as open, because `parseJobStatus` resolves both to
 * `pending`: a
 * conversation whose job could not be read server-side (`jobStatus` is
 * `omitempty`) must not cost a contractor a quote he is entitled to send —
 * the server's own 400 is the backstop for that case.
 */
export function isJobOpenForQuotes(status: JobStatus): boolean {
  return status === "pending" || status === "quoted";
}

/* ------------------------------------------------------------------------- */

export type QuoteStatus = "pending" | "accepted" | "rejected" | "expired";

const QUOTE_STATUSES: readonly QuoteStatus[] = ["pending", "accepted", "rejected", "expired"];

export function parseQuoteStatus(value: string | null | undefined): QuoteStatus {
  if (!value) return "pending";
  const normalized = squash(value);
  return QUOTE_STATUSES.find((status) => status === normalized) ?? "pending";
}

export function quoteStatusLabel(status: QuoteStatus): string {
  return labelFor("quote", status);
}

/* ------------------------------------------------------------------------- */

/* The trade taxonomy is shoku-ui's TRADES, for the same reason the status
 * WORDING above lives in statusTone rather than here: this file used to hold
 * three declarations of it (union, array, labels), the homeowner site a fourth,
 * and nothing compared any of them. Values and labels both come from there now;
 * only the tolerant parsing below is this site's. */
export type JobCategory = Trade;

export const JOB_CATEGORIES: readonly JobCategory[] = TRADES.map((trade) => trade.value);
export { LAUNCH_TRADES };

/** Unlike statuses, an unrecognised category returns null rather than a default —
 *  guessing here would file a job under the wrong trade. Both the category value
 *  and the incoming value are squashed for comparison. */
export function parseJobCategory(value: string | null | undefined): JobCategory | null {
  if (!value) return null;
  const normalized = squash(value);
  return (
    JOB_CATEGORIES.find(
      (category) =>
        squash(category) === normalized || squash(tradeLabel(category)) === normalized,
    ) ?? null
  );
}

export function jobCategoryLabel(category: JobCategory): string {
  return tradeLabel(category);
}

/** Best-effort display for a raw wire value, falling back to the value itself. */
export function jobCategoryDisplay(value: string | null | undefined): string {
  const parsed = parseJobCategory(value);
  return parsed ? tradeLabel(parsed) : (value ?? "");
}

/* ------------------------------------------------------------------------- */

/* Urgency has no Go enum — `types.Job.Urgency` is a bare string written by the
 * homeowner app. The mobile app therefore title-cases the four values it knows
 * and echoes anything else through unchanged rather than mislabelling it, which
 * is the behaviour reproduced here. */
const URGENCY_LABELS: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

export function urgencyLabel(value: string | null | undefined): string {
  if (!value) return "";
  return URGENCY_LABELS[value.toLowerCase()] ?? value;
}

/* ------------------------------------------------------------------------- */

/** A contractor is either on the marketplace or taking direct bookings — never
 *  both. The whole booking-settings area is unreachable in marketplace mode. */
export type OperatingMode = "marketplace" | "bookable";

export function parseOperatingMode(value: string | null | undefined): OperatingMode {
  return squash(value ?? "") === "bookable" ? "bookable" : "marketplace";
}

/** The full vocabulary from `types.BookingStatus` — all thirteen values. */
export type BookingStatus =
  | "requested"
  | "accepted"
  | "declined"
  | "expired"
  | "paymentPending"
  | "paymentFailed"
  | "paid"
  | "inProgress"
  | "completed"
  | "cancelledByUser"
  | "cancelledByContractor"
  | "refunded"
  | "noShow";

const BOOKING_STATUSES: readonly BookingStatus[] = [
  "requested",
  "accepted",
  "declined",
  "expired",
  "paymentPending",
  "paymentFailed",
  "paid",
  "inProgress",
  "completed",
  "cancelledByUser",
  "cancelledByContractor",
  "refunded",
  "noShow",
];

export function parseBookingStatus(value: string | null | undefined): BookingStatus {
  if (!value) return "requested";
  const normalized = squash(value);
  return BOOKING_STATUSES.find((status) => status.toLowerCase() === normalized) ?? "requested";
}

export function bookingStatusLabel(status: BookingStatus): string {
  return labelFor("booking", status);
}

/** The three statuses a contractor can still cancel from. */
export function canCancelBooking(status: BookingStatus): boolean {
  return status === "accepted" || status === "paymentPending" || status === "paid";
}

/* ------------------------------------------------------------------------- */

/* Contractor account status. The vocabulary is written by admincli and the
 * registration handler — "pending_verification" on sign-up, then "active" on
 * approval or "rejected" on decline. Note that `status == "active"` is NOT the
 * same as verified: the badge keys off `isVerified` first and only consults
 * status to tell "rejected" apart from "still waiting". */
export type AccountStatus = "pendingVerification" | "active" | "rejected";

export function parseAccountStatus(value: string | null | undefined): AccountStatus {
  const normalized = squash(value ?? "");
  if (normalized === "active") return "active";
  if (normalized === "rejected") return "rejected";
  return "pendingVerification";
}

export type VerificationBadge = "verified" | "rejected" | "pending";

/** Mirrors `verification_badge.dart`: verified wins, then rejected, else pending. */
export function verificationBadge(input: {
  isVerified: boolean;
  status: string | null | undefined;
}): VerificationBadge {
  if (input.isVerified) return "verified";
  return parseAccountStatus(input.status) === "rejected" ? "rejected" : "pending";
}

/* Bank verification, from `types.BankVerificationStatus` usage in the bank
 * handler: pending while Paystack is consulted, verified once the subaccount
 * exists, failed when validation is rejected (persisted BEFORE the 422, so a
 * retry is safe and the UI should re-read rather than trust local form state). */
export type BankVerificationStatus = "none" | "pending" | "verified" | "failed";

export function parseBankVerificationStatus(
  value: string | null | undefined,
): BankVerificationStatus {
  const normalized = squash(value ?? "");
  if (normalized === "verified") return "verified";
  if (normalized === "failed" || normalized === "bankverificationfailed") return "failed";
  if (normalized === "pending") return "pending";
  return "none";
}
